import type { Job } from '../types/site';

export type RecurrenceIntervalUnit = 'days' | 'weeks' | 'months';
export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addRecurrenceInterval(
  date: Date,
  count: number,
  unit: RecurrenceIntervalUnit,
): Date {
  const d = new Date(date);
  const n = Math.max(1, count);
  if (unit === 'days') {
    d.setDate(d.getDate() + n);
  } else if (unit === 'weeks') {
    d.setDate(d.getDate() + n * 7);
  } else {
    d.setMonth(d.getMonth() + n);
  }
  return startOfDay(d);
}

export function computeNextRunAt(
  from: Date,
  intervalCount: number,
  intervalUnit: RecurrenceIntervalUnit,
): Date {
  return addRecurrenceInterval(startOfDay(from), intervalCount, intervalUnit);
}

export function formatRecurrenceLabel(
  intervalCount: number,
  intervalUnit: RecurrenceIntervalUnit,
): string {
  const n = Math.max(1, intervalCount);
  const unitLabel = n === 1
    ? intervalUnit.replace(/s$/, '')
    : intervalUnit;
  return `Every ${n} ${unitLabel}`;
}

export function toDateKey(date: Date): string {
  const d = startOfDay(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseDateKey(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  return startOfDay(new Date(y, (m ?? 1) - 1, d ?? 1));
}

export function pickJobForRecurrenceRule(jobs: Job[] | undefined, ruleId: string): Job | null {
  const related = (jobs ?? []).filter((j) => j.recurrenceRuleId === ruleId);
  if (related.length === 0) return null;

  const pending = related
    .filter((j) => j.status === 'pending' || j.status === 'in-progress')
    .sort((a, b) => {
      const aTime = a.dueDate ? new Date(a.dueDate).getTime() : 0;
      const bTime = b.dueDate ? new Date(b.dueDate).getTime() : 0;
      return aTime - bTime;
    });
  if (pending.length > 0) return pending[0];

  return related.sort((a, b) => {
    const aTime = a.dueDate ? new Date(a.dueDate).getTime() : 0;
    const bTime = b.dueDate ? new Date(b.dueDate).getTime() : 0;
    return bTime - aTime;
  })[0];
}

export function isPastEndDate(nextRun: Date, endDate: Date | undefined): boolean {
  if (!endDate) return false;
  return startOfDay(nextRun).getTime() > startOfDay(endDate).getTime();
}

/** True when `date` falls on the recurrence schedule grid. */
export function isValidOccurrenceDate(
  startDate: Date,
  intervalCount: number,
  intervalUnit: RecurrenceIntervalUnit,
  date: Date,
  maxSteps = 500,
): boolean {
  let cursor = startOfDay(startDate);
  const target = startOfDay(date).getTime();
  if (target < cursor.getTime()) return false;
  for (let i = 0; i < maxSteps; i++) {
    if (cursor.getTime() === target) return true;
    if (cursor.getTime() > target) return false;
    cursor = computeNextRunAt(cursor, intervalCount, intervalUnit);
  }
  return false;
}

export function getLatestInstanceDueDate(jobs: Job[] | undefined, ruleId: string): Date | null {
  const related = (jobs ?? []).filter((j) => j.recurrenceRuleId === ruleId && j.dueDate);
  if (related.length === 0) return null;
  return related.reduce((latest, j) => {
    const d = startOfDay(j.dueDate!);
    return !latest || d.getTime() > latest.getTime() ? d : latest;
  }, null as Date | null);
}

/** Upcoming scheduled occurrence dates the user can pick as the series end. */
export function getSelectableEndOccurrences(
  rule: {
    startDate: Date;
    intervalCount: number;
    intervalUnit: RecurrenceIntervalUnit;
    nextRunAt: Date;
    endDate?: Date;
  },
  jobs: Job[] | undefined,
  ruleId: string,
  count = 24,
): Date[] {
  const latest = getLatestInstanceDueDate(jobs, ruleId);
  let cursor = latest
    ? computeNextRunAt(latest, rule.intervalCount, rule.intervalUnit)
    : startOfDay(rule.startDate);
  const today = startOfDay(new Date());
  if (cursor.getTime() < today.getTime()) {
    cursor = startOfDay(rule.nextRunAt);
  }
  if (cursor.getTime() < startOfDay(rule.startDate).getTime()) {
    cursor = startOfDay(rule.startDate);
  }

  const dates: Date[] = [];
  for (let i = 0; i < count; i++) {
    if (rule.endDate && cursor.getTime() > startOfDay(rule.endDate).getTime()) break;
    dates.push(new Date(cursor));
    cursor = computeNextRunAt(cursor, rule.intervalCount, rule.intervalUnit);
  }
  return dates;
}

/** Occurrence dates for End Job: materialized instances plus upcoming schedule. */
export function getEndJobOccurrenceOptions(
  rule: {
    startDate: Date;
    intervalCount: number;
    intervalUnit: RecurrenceIntervalUnit;
    nextRunAt: Date;
    endDate?: Date;
  },
  jobs: Job[] | undefined,
  ruleId: string,
  count = 24,
): Date[] {
  const related = (jobs ?? []).filter((j) => j.recurrenceRuleId === ruleId && j.dueDate);
  const instanceDates = related.map((j) => startOfDay(j.dueDate!));
  const upcoming = getSelectableEndOccurrences(rule, jobs, ruleId, count);
  const merged = new Map<string, Date>();
  [...instanceDates, ...upcoming].forEach((d) => merged.set(toDateKey(d), d));
  return Array.from(merged.values()).sort((a, b) => a.getTime() - b.getTime());
}

export function formatOccurrenceDateInput(date: Date): string {
  const d = startOfDay(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
